import { Component } from '@theme/component';

/**
 * Wraps a product-list section's header (with TCG category buttons) and its
 * per-collection product panels. Intercepts clicks on `.tcg-nav__item` buttons
 * that have a matching `[data-collection-handle]` panel inside this component
 * and swaps the visible panel instead of navigating away. If a button's href
 * has no matching panel (or JS hasn't loaded), the link navigates normally.
 *
 * @extends {Component}
 */
class ProductListTabs extends Component {
  /**
   * @param {PointerEvent} event
   */
  tabSelect(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const button = target.closest('.tcg-nav__item');
    if (!(button instanceof HTMLElement)) return;

    const handle = button.dataset.collectionHandle;
    if (!handle) return;

    const panel = this.querySelector(
  `.product-list-tabs__panel[data-collection-handle="${CSS.escape(handle)}"]`
);
    if (!panel) return;

    event.preventDefault();

    if (button.getAttribute('aria-current') === 'page') return;

    for (const item of this.querySelectorAll('.tcg-nav__item')) {
      const isActive = item === button;
      item.classList.toggle('tcg-nav__item--active', isActive);
      item.setAttribute('aria-current', isActive ? 'page' : 'false');
    }

    for (const otherPanel of this.querySelectorAll(
  '.product-list-tabs__panel[data-collection-handle]'
)) {
      if (!(otherPanel instanceof HTMLElement)) continue;
      const isInactive = otherPanel !== panel;
      otherPanel.classList.toggle('product-list-tabs__panel--inactive', isInactive);
      if (isInactive) {
        otherPanel.setAttribute('aria-hidden', 'true');
      } else {
        otherPanel.removeAttribute('aria-hidden');
      }
    }

    this.#resyncCarousel(panel);
  }

  /**
   * `<slideshow-component>` (carousel-layout panels, e.g. Singles) tracks
   * which slide is visible via an `aria-hidden` attribute it manages itself,
   * driven by an IntersectionObserver set up once when it first connects to
   * the DOM. Because every tab panel is rendered up front and only ever
   * toggled via the `product-list-tabs__panel--inactive` class, a panel that
   * is (or was) inactive has that tracking computed while off-screen, and
   * nothing afterwards re-triggers it — so simply reactivating a panel again
   * can leave every slide still marked `aria-hidden="true"` (which CSS
   * renders as not visible), i.e. a blank panel.
   *
   * `select()` is the component's own public method for marking a slide
   * visible, but it intentionally no-ops when the requested index already
   * equals its cached `current` index — exactly the stale state left behind
   * here. Resetting `current` (also a public property) first forces that
   * mismatch so `select()` runs its normal logic and marks the first slide
   * visible again. This only calls the component's existing public API and
   * toggles the attributes/state it already owns — no DOM is added, removed,
   * or replaced. No-op for grid-layout panels (e.g. Sealed Products), which
   * have no `slideshow-component`.
   *
   * @param {Element} panel
   */
  #resyncCarousel(panel) {
    const slideshow = /** @type {any} */ (panel.querySelector('slideshow-component'));
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const MAX_FRAMES = 10;
    let frames = 0;

    const waitForLayout = () => {
      if (frames++ >= MAX_FRAMES) return;
      if (panel.classList.contains('product-list-tabs__panel--inactive')) return;

      const scroller =
        slideshow.querySelector('slideshow-slides[ref="scroller"]') ?? slideshow.querySelector('[ref="scroller"]');

      if (!scroller || scroller.getBoundingClientRect().width === 0) {
        requestAnimationFrame(waitForLayout);
        return;
      }

      slideshow.current = -1;
      slideshow.select(0, undefined, { animate: false });
    };

    requestAnimationFrame(waitForLayout);
  }
}

if (!customElements.get('product-list-tabs-component')) {
  customElements.define('product-list-tabs-component', ProductListTabs);
}
